import { useTranslation } from 'gatsby-plugin-react-i18next'
import { find, isEmpty, orderBy, random } from 'lodash'
import React, { useEffect, useState } from 'react'
import Select, { OnChangeValue } from 'react-select'

import { SUBURB_DEFAULT_NEIGHBORHOODS } from '../../constants'
import useHomepage from '../../hooks/useHomepage'
import useVisibility from '../../hooks/useVisibility'
import { CityType, NeighborhoodType, RestaurantType } from '../../types'
import ScrollAnimation from '../scroll-animation/Container'
import Logos from './Logos'
import MapboxGLMap from './MapboxGLMap'
import NeighborhoodTabs from './NeighborhoodTabs'
import Restaurant from './Restaurant'
import './map.less'

const MapContainer = () => {
  const {
    cities,
    activeCityName,
    setActiveCity,
    activeRestaurantId,
    setActiveRestaurantId,
    productOffering,
    citySelectorEnabled,
    animationProps,
  } = useHomepage()

  const { t } = useTranslation()
  const neighborhoods = orderBy(SUBURB_DEFAULT_NEIGHBORHOODS, 'position', 'asc')

  const [currentRestaurants, setCurrentRestaurants] = useState<RestaurantType[]>([])
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<NeighborhoodType>(neighborhoods[0])
  const [isSectionInView, parentElement] = useVisibility<HTMLDivElement>(100)

  useEffect(() => {
    if (isEmpty(cities)) return
    const activeCity = find(cities, (city) => city.name.toLowerCase() === activeCityName.toLowerCase())
    const restaurants = activeCity !== undefined ? activeCity.restaurants : []
    const rand = random(restaurants.length - 1)
    const activeRestaurant = restaurants[rand]

    setCurrentRestaurants(restaurants)
    setActiveRestaurantId(activeRestaurant ? activeRestaurant.id : '')
  }, [cities, activeCityName])

  const onCityChangeHandler = (option: OnChangeValue<typeof selectedOption, false>) => {
    if (option !== null) {
      setActiveCity(option.value)
    }
  }

  const activeRestaurant = find(currentRestaurants, (r: RestaurantType) => r.id === activeRestaurantId)
  const options = cities.map((c: CityType) => {
    return {
      value: c.name,
      label: c.name,
    }
  })
  const selectedOption = {
    value: activeCityName,
    label: activeCityName,
  }

  const onDinnerPage = productOffering === 'dinner'

  return (
    <div className={`map map--${productOffering}`} ref={parentElement}>
      <div id="map" />
      <ScrollAnimation {...animationProps}>
        <div className="map__title">{t("Discover what's near you")}</div>
      </ScrollAnimation>
      <ScrollAnimation {...animationProps}>
        {onDinnerPage ? (
          <div className="map__subtitle">
            Imagine a dinner menu with plenty of options for everyone. Now imagine <span>saving 30%</span> while helping
            restaurants thrive.
          </div>
        ) : (
          <>
            {citySelectorEnabled && (
              <div className="custom-select__wrapper">
                <Select
                  classNamePrefix="custom-select"
                  isSearchable={false}
                  value={selectedOption}
                  onChange={onCityChangeHandler}
                  options={options}
                />
              </div>
            )}
          </>
        )}
      </ScrollAnimation>
      <ScrollAnimation {...animationProps}>
        <div className="map__wrapper">
          {onDinnerPage ? (
            <NeighborhoodTabs
              neighborhoods={neighborhoods}
              onChange={setSelectedNeighborhood}
              selectedNeighborhood={selectedNeighborhood}
            />
          ) : null}
          <div className="map__element">
            <MapboxGLMap
              activeCityName={activeCityName}
              activeRestaurant={activeRestaurant}
              restaurants={currentRestaurants}
              selectedNeighborhood={selectedNeighborhood}
            />
            {isEmpty(currentRestaurants) && <div className="map__blur" />}
          </div>
          <Restaurant restaurant={activeRestaurant} isSectionInView={isSectionInView} />
        </div>
      </ScrollAnimation>
      {onDinnerPage ? <Logos /> : ''}
    </div>
  )
}

export default MapContainer
